import { Alert, GetConversationTasksSchema } from '@api';
import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { toMentionPlainText } from '@terminal/components/chat/chat-input/mentions/markers';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { getCardDate } from '@terminal/utils/card-utils/card-utils';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import sharedStyles from '../../AlertsCard.module.scss';
import { extractContentPreview } from '../../utils/extract-content.preview';
import { getErrorText } from '../../utils/get-error-text';
import styles from './AlertsAlfredCard.module.scss';

interface AlertsAlfredCardProps {
  alertEvent: Alert;
  conversationData?: GetConversationTasksSchema;
  dropdown?: React.ReactNode;
  isLoading: boolean;
}

export const AlertsAlfredCard = ({
  alertEvent,
  conversationData,
  dropdown,
  isLoading,
}: AlertsAlfredCardProps) => {
  const { t } = useTranslation(['alerts', 'whatsNew'], {
    useSuspense: false,
  });
  const mentionsEnabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2103-alfred-mentions')
  );
  const { created_at: createdAt, read, metadata } = alertEvent;
  const requestId = metadata?.payload?.request_id;

  const task =
    conversationData?.tasks?.find(t => t.request_id === requestId) ??
    conversationData?.tasks?.[0];
  const taskState = task?.last_state;

  const isError =
    taskState?.status === 'Error' || taskState?.status === 'Fatal';

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '60px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!taskState) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <span>{t('alerts:alfredCard.title')}</span>
        </div>
        <div className={sharedStyles.headerRight}>
          <span className={sharedStyles.dateSpan}>
            {getCardDate(new Date(createdAt))}
          </span>
          {dropdown}
        </div>
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <div className={styles.alfredLogo}>
            <Icon iconName="Alfred" size={20} fillColor="var(--icon-primary)" />
          </div>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={clsx(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
            })}
          >
            {toMentionPlainText(
              task?.input ?? conversationData?.name ?? '',
              mentionsEnabled
            )}
          </p>
          <div className={styles.secondaryText}>
            {isError ? (
              <div className={styles.errorWrapper}>
                <Icon
                  fillColor={read ? 'var(--icon-soft)' : 'var(--icon-warning)'}
                  iconName="InformationCircle"
                  size={12}
                />
                <span
                  className={clsx(styles.errorText, {
                    [styles.read]: read,
                  })}
                >
                  {getErrorText(t, taskState?.content)}
                </span>
              </div>
            ) : (
              extractContentPreview(taskState?.content ?? '')
            )}
          </div>
        </div>
      </div>
    </>
  );
};
